import { FC, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext, useFieldArray, useWatch } from 'react-hook-form';
import { push } from 'connected-react-router';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
// Icons
import { MdDragIndicator } from 'react-icons/md';
// Types
import QuizTypes from 'types/QuizTypes';
import QuestionTypes from 'types/QuestionTypes';
import MediaFormats from 'types/MediaFormats';
import QuestionFormats from 'types/QuestionFormats';
// Models
import IBucket from 'models/Bucket';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
// Actions
import { appActions } from 'store/app/appSlice';
// Selector
import { selectQuiz } from 'store/quiz/quizzesSelectors';
// Components
import { Checkbox, Input, Select } from 'components/Controls';
import ImageUpload from 'components/ImageUpload';
import VideoUpload from 'components/VideoUpload';
import AudioUpload from 'components/AudioUpload';
import Answer from '../Answer';
import MultiLanguageTextField from 'components/MultiLanguageTextField';
import Slider from 'components/Slider';
import CorrectAnswers from '../CorrectAnswers';
import TopListItems from '../TopListItems';
import { Button, IconButton } from 'components/Buttons';
import TimeoutField from '../TimeoutField';
// Service
import QuestionService from 'services/question.service';
// Utilities
import { isRequired, noWhitespace } from 'utilities/validation';
import { expandAccordion } from 'utilities/utilities';
// framer-motion
import { Reorder } from 'framer-motion/dist/framer-motion';

type Props = {
  questionRef: string;
}

const SimpleQuestionForm: FC<Props> = ({ questionRef }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const quiz = useAppSelector(selectQuiz);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  const [duration, setDuration] = useState<number>(0);

  const { control, formState: { errors }, watch, setValue } = useFormContext();

  const watchedQuestions = watch('questions');
  const questionIndex = watchedQuestions.findIndex((question: any) => question.ref === questionRef);

  const { fields, append, remove } = useFieldArray<any>({
    control,
    name: `questions[${questionIndex}].answers`,
  });

  const getQuestionError = useCallback((field: string) => {
    if (!errors || !errors.questions) return '';
    const question = (errors as any).questions[questionIndex];
    return question && question[field] ? question[field].message : '';
    // eslint-disable-next-line
  }, [errors]);

  const initialAnswer = useMemo(() => {
    const answer: any = { title: '', correct: false };
    if (quiz?.buckets) {
      answer['bucketScores'] = quiz.buckets.map((bucket: IBucket) => ({
        ref: bucket.ref,
        score: 0,
      }));
    }
    return answer;
    // eslint-disable-next-line
  }, [quiz]);

  const handleAdd = () => {
    append({ ...initialAnswer, ref: uuidv4() });
    setTimeout(() => {
      const lastAnswerInput = document.getElementById(`questions[${questionIndex}].answers.${fields.length}.title`);
      lastAnswerInput?.focus();
    }, 10);
  };

  const handleDelete = (index: number) => {
    remove(index);
  };

  const watchedQuestionType = watch(`questions[${questionIndex}].type`);
  const watchedQuestionFormat = watch(`questions[${questionIndex}].format`);
  const watchedQuestionTimeout = watch(`questions[${questionIndex}].timeout`);
  const watchedQuizType = watch('type');
  const defaultQuestionTimeout = watchedQuizType === QuizTypes.Prediction ? 30 : 10;

  useEffect(() => {
    if (watchedQuestionType === QuestionTypes.ShortText) {
      setValue(`questions[${questionIndex}].answers`, []);
    }
    // eslint-disable-next-line
  }, [watchedQuestionType]);

  const watchedItems = useWatch({
    control,
    name: `questions[${questionIndex}].answers`,
  });

  const reorder = (newItems: any) => {
    setValue(`questions[${questionIndex}].answers`, newItems);
  };

  const watchedImage = watch(`questions.${questionIndex}.image`);
  const watchedVideo = watch(`questions.${questionIndex}.video`);
  const watchedAudio = watch(`questions.${questionIndex}.audio`);
  const watchedAudioPlaybackRate = watch(`questions.${questionIndex}.audioPlaybackRate`);
  const watchedAudioStartTime = watch(`questions.${questionIndex}.audioStartTime`);
  const watchedAudioEndTime = watch(`questions.${questionIndex}.audioEndTime`);

  useEffect(() => {
    const audioEl = audioRef.current;
    if (!audioEl) return;

    const handleLoadedMetadata = () => {
      const duration = Math.ceil(audioEl.duration);
      setDuration(duration);
    };

    audioEl.addEventListener('loadedmetadata', handleLoadedMetadata);

    return () => {
      audioEl.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [audioRef, watchedAudio]);

  useEffect(() => {
    if (!watchedAudio) {
      setValue(`questions.${questionIndex}.audioStartTime`, 0);
      setValue(`questions.${questionIndex}.audioEndTime`, 0);
      setValue(`questions.${questionIndex}.audioPlaybackRate`, 1);
      setDuration(0);
    }
  }, [watchedAudio, questionIndex, setValue]);

  const onChangeRange = (_: any, newValue: any) => {
    setValue(`questions.${questionIndex}.audioStartTime`, newValue[0]);
    setValue(`questions.${questionIndex}.audioEndTime`, newValue[1]);
  };

  const goToQuestionsSettings = () => {
    dispatch(push({ search: 'tab=quiz' }));
    setTimeout(() => {
      dispatch(appActions.setQuizFormMenuItem('questionsSettings'));
      expandAccordion('questionsSettings');
    }, 300);
  };

  const isPollQuizzes = [QuizTypes.Poll, QuizTypes.RankingPoll].includes(watchedQuizType);
  const isRankingPollQuizzes = watchedQuizType === QuizTypes.RankingPoll;
  const isScoringQuizzes = [QuizTypes.Scoring, QuizTypes.Prediction, QuizTypes.Streak].includes(watchedQuizType);
  const isTeamSelectorQuiz = watchedQuizType === QuizTypes.TeamSelector;
  const isStreakQuiz = watchedQuizType === QuizTypes.Streak;
  const isAnyAnswerCorrect = fields.some((field: any) => field.correct);

  const showQuestionScoreInput = watchedQuestionType !== QuestionTypes.TopList
    && [QuizTypes.Scoring, QuizTypes.Prediction, QuizTypes.BeatTheExpert, QuizTypes.Streak].includes(watchedQuizType);

  const showAnswersScoringHint = ![QuestionTypes.Ranking, QuestionTypes.TopList].includes(watchedQuestionType)
    && [QuizTypes.Scoring, QuizTypes.BeatTheExpert, QuizTypes.Streak].includes(watchedQuizType);

  const showQuestionTypeInput = watchedQuestionType !== QuestionTypes.TopList
    && [QuizTypes.Personality, QuizTypes.Prediction, QuizTypes.Scoring, QuizTypes.Streak].includes(watchedQuizType);

  const showMediaFields = watchedQuizType !== QuizTypes.BeatTheExpert && watchedQuestionType !== QuestionTypes.TopList;

  const isRequiredMedia = !watchedImage && !watchedVideo && !watchedAudio && !isPollQuizzes;

  const sliderValue = useMemo(() => {
    const startTime = watchedAudioStartTime || 0;
    const endTime = watchedAudioEndTime || 0;
    return [startTime, endTime];
  }, [watchedAudioStartTime, watchedAudioEndTime]);

  if (questionIndex === undefined || questionIndex < 0) return null;

  return (
    <div className="w-full flex flex-wrap -mx-6 -y-4 px-6 pb-3">
      <div className={`w-full px-6 ${showMediaFields ? 'landscape:w-1/2 tablet:w-full laptop:w-7/12' : ''}`}>
        <MultiLanguageTextField
          rules={{ required: isRequired, validate: noWhitespace }}
          name={`questions.${questionIndex}.title`}
          label={t('labels.question')}
          required={true}
          error={Boolean(getQuestionError('title'))}
          errorText={getQuestionError('title')}
          customKey={`questions.${questionRef}.title`}
          className="mb-4"
        />
        {showQuestionScoreInput && (
          <Controller
            control={control} name={`questions.${questionIndex}.score`}
            render={({ field: { onChange, value } }) => (
              <Input
                type="number"
                label={t('labels.score')} id={`questions.${questionIndex}.score`} name={`questions.${questionIndex}.score`}
                value={value}
                onChange={onChange}
                helperText={watchedQuestionTimeout
                  ? t('pages.quizForm.questionScoreHintWithTimeout', { score: (watchedQuestionTimeout || defaultQuestionTimeout) * 100 })
                  : t('pages.quizForm.questionScoreHint', { score: watchedQuizType === QuizTypes.Prediction ? '3,000' : '1,000' })
                }
              />
            )}
          />
        )}
        <TimeoutField questionIndex={questionIndex} />
        {showQuestionTypeInput ? (
          <Controller
            control={control} name={`questions.${questionIndex}.type`}
            render={({ field }) => (
              <Select
                {...field}
                label={t('labels.type')} id={`questions.${questionIndex}.type`}
                options={QuestionService.questionTypeOptions(watchedQuizType)}
              />
            )}
          />
        ) : null}
        {(isScoringQuizzes || isPollQuizzes || isRankingPollQuizzes || isTeamSelectorQuiz) && watchedQuestionType === QuestionTypes.SingleChoice && (
          <Controller
            control={control} name={`questions.${questionIndex}.renderAsDropdown`}
            render={({ field: { value, onChange } }) => (
              <Checkbox
                className="mb-0"
                label={t('labels.renderAsDropdown')}
                id={`questions.${questionIndex}.renderAsDropdown`}
                name={`questions.${questionIndex}.renderAsDropdown`}
                checked={value}
                onChange={onChange}
              />
            )}
          />
        )}
        {[QuizTypes.Scoring, QuizTypes.Streak].includes(watchedQuizType) && watchedQuestionType === QuestionTypes.ShortText && (
          <Fragment>
            <CorrectAnswers questionIndex={questionIndex} questionRef={questionRef} />
            {watchedQuizType === QuizTypes.Scoring && (
              <MultiLanguageTextField
                name={`questions.${questionIndex}.config.clue`}
                label={t('labels.clue')}
                customKey={`questions.${questionRef}.config.clue`}
                className="mb-4"
                helperText={t('pages.quizForm.clueHint')}
              />
            )}
          </Fragment>
        )}
        {watchedQuizType === QuizTypes.Prediction && watchedQuestionType === QuestionTypes.ShortText && (
          <Fragment>
            <Controller
              control={control} name={`questions.${questionIndex}.format`}
              render={({ field }) => (
                <Select
                  {...field}
                  label={t('labels.format')} id={`questions.${questionIndex}.fomat`}
                  options={[{ value: '', label: t('labels.freeText') }, ...QuestionService.questionFormatOptions()]}
                />
              )}
            />
            {!!watchedQuestionFormat && watchedQuestionFormat === QuestionFormats.Score ? (
              <div className="flex gap-4">
                <Controller
                  control={control} name={`questions.${questionIndex}.scorePlaceholder1`}
                  rules={{ required: isRequired }}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      label={t('pages.quizForm.scorePlaceholder', { index: 1 })} id={`questions.${questionIndex}.scorePlaceholder1`} name={`questions.${questionIndex}.scorePlaceholder1`}
                      value={value}
                      onChange={onChange}
                      required
                      error={Boolean(errors.questions?.[questionIndex]?.scorePlaceholder1)}
                      errorText={errors.questions?.[questionIndex]?.scorePlaceholder1?.message}
                    />
                  )}
                />
                <Controller
                  control={control} name={`questions.${questionIndex}.scorePlaceholder2`}
                  rules={{ required: isRequired }}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      label={t('pages.quizForm.scorePlaceholder', { index: 2 })} id={`questions.${questionIndex}.scorePlaceholder2`} name={`questions.${questionIndex}.scorePlaceholder2`}
                      value={value}
                      onChange={onChange}
                      required
                      error={Boolean(errors.questions?.[questionIndex]?.scorePlaceholder2)}
                      errorText={errors.questions?.[questionIndex]?.scorePlaceholder2?.message}
                    />
                  )}
                />
              </div>
            ) : null}
          </Fragment>
        )}
        {watchedQuestionType === QuestionTypes.TopList ? (
          <TopListItems questionIndex={questionIndex} />
        ) : null}
        {![QuestionTypes.ShortText, QuestionTypes.TopList].includes(watchedQuestionType) && !isRankingPollQuizzes ? (
          <Fragment>
            <span className="divider"></span>
            <h2 className="text-white text-xl font-bold leading-6 my-4">{t('labels.answers')}</h2>
            {showAnswersScoringHint ? (
              <span className="block text-gray-400 text-sm font-light leading-5 -mt-2 mb-4">{t('pages.quizForm.answersScoringHint')}</span>
            ) : null}
            {watchedQuestionType === QuestionTypes.Ranking ? (
              <span className="block text-gray-400 text-sm font-light leading-5 -mt-2 mb-4">{t('pages.quizForm.rankingQuestionTypeHint')}</span>
            ) : null}
            <Reorder.Group axis="y" values={watchedItems} as="div" onReorder={reorder} className="flex flex-col gap-2 mb-4">
              {watchedItems.map((item: any, idx: number) => (
                <Reorder.Item
                  key={`${questionIndex}-answer-item-${item.ref}`} as="div"
                  className="flex items-center gap-4"
                  value={item}
                >
                  <IconButton onClick={() => {}}>
                    <MdDragIndicator />
                  </IconButton>
                  <Answer
                    questionIndex={questionIndex}
                    questionRef={questionRef}
                    index={idx}
                    onDelete={fields.length > 2 ? handleDelete : undefined}
                  />
                </Reorder.Item>
              ))}
            </Reorder.Group>
            {[QuizTypes.Scoring, QuizTypes.BeatTheExpert, QuizTypes.Streak].includes(watchedQuizType) && isAnyAnswerCorrect ? (
              <MultiLanguageTextField
                name={`questions.${questionIndex}.correctAnswerDescription`}
                label={t('pages.quizForm.correctAnswerDescriptionLabel')}
                helperText={t('pages.quizForm.correctAnswerDescriptionHint')}
                customKey={`questions.${questionRef}.correctAnswerDescription`}
                className="mb-4"
                multiline
                rows={3}
              />
            ) : null}
            <div className="text-right">
              <Button variant="contained" onClick={handleAdd}>{t('buttons.addAnswer')}</Button>
            </div>
          </Fragment>
        ) : null}
        {isRankingPollQuizzes ? (
          <Button variant="contained" className="mt-4" onClick={goToQuestionsSettings}>
            {t('buttons.answersOptions')}
          </Button>
        ) : null}
      </div>
      {showMediaFields ? (
        <div
          className="
            w-full landscape:w-1/2 tablet:w-full laptop:w-5/12
            landscape:border-l tablet:border-l-0 laptop:border-l
            px-6
          "
          style={{ borderColor: 'rgba(255,255,255,0.2)' }}
        >
          <Controller
            control={control}
            name={`questions.${questionIndex}.image`}
            rules={{ required: isRequiredMedia ? isRequired : false }}
            render={({ field }) => (
              <ImageUpload
                {...field}
                format={MediaFormats.Question}
                label={isStreakQuiz ? 'pages.quizForm.preAnswerImage' : 'labels.image'}
                required={isRequiredMedia}
                error={isRequiredMedia && Boolean(getQuestionError('image'))}
                errorText={getQuestionError('image')}
                helperText={isStreakQuiz ? t('pages.quizForm.preAnswerImageHint') : ''}
              />
            )}
          />
          {isScoringQuizzes ? (
            <Controller
              control={control}
              name={`questions.${questionIndex}.postAnswerImage`}
              render={({ field }) => (
                <ImageUpload
                  {...field}
                  format={MediaFormats.Question}
                  label="pages.quizForm.postAnswerImage"
                  helperText={t('pages.quizForm.postAnswerImageHint')}
                />
              )}
            />
          ) : null}
          {!isRankingPollQuizzes ? (
            <Fragment>
              <Controller
                control={control} name={`questions.${questionIndex}.video`}
                rules={{ required: isRequiredMedia ? isRequired : false }}
                render={({ field }) => (
                  <VideoUpload
                    {...field}
                    label="labels.video"
                    required={isRequiredMedia}
                    error={isRequiredMedia && Boolean(getQuestionError('video'))}
                    helperText="pages.quizForm.questionVideoHint"
                  />
                )}
              />
              <Controller
                control={control} name={`questions.${questionIndex}.audio`}
                rules={{ required: isRequiredMedia ? isRequired : false }}
                render={({ field }) => (
                  <AudioUpload
                    {...field}
                    key={watchedAudioPlaybackRate}
                    audioRef={audioRef}
                    label="labels.audio"
                    required={isRequiredMedia}
                    error={isRequiredMedia && Boolean(getQuestionError('audio'))}
                    helperText="pages.quizForm.questionAudioHint"
                    playbackRate={watchedAudioPlaybackRate}
                    startTime={watchedAudioStartTime}
                    endTime={watchedAudioEndTime}
                  />
                )}
              />
              <div className="mb-4">
                <span className={`block text-white text-sm font-semibold mb-1 ${!watchedAudio ? 'opacity-50' : ''}`}>
                  {t('pages.quizForm.audioPlaybackRangeLabel')}
                </span>
                <Slider
                  value={watchedAudio ? sliderValue : [0, 0]}
                  onChange={onChangeRange}
                  min={0}
                  max={duration}
                  step={1}
                  disabled={!watchedAudio}
                  valueLabelDisplay="auto"
                />
                <span className="block text-gray-400 text-sm font-light leading-5 mt-1">{t('pages.quizForm.audioPlaybackRangeHint')}</span>
              </div>
              <Controller
                control={control} name={`questions.${questionIndex}.audioPlaybackRate`}
                render={({ field: { onChange, value } }) => (
                  <Select
                    id={`questions.${questionIndex}.audioPlaybackRate`}
                    name={`questions.${questionIndex}.audioPlaybackRate`}
                    options={audioPlaybackRateOptions}
                    value={value ?? 1}
                    onChange={onChange}
                    label={t('pages.quizForm.audioPlaybackRateLabel')}
                    helperText={t('pages.quizForm.audioPlaybackRateHint')}
                    disabled={!watchedAudio}
                  />
                )}
              />
            </Fragment>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default SimpleQuestionForm;

const audioPlaybackRateOptions = [
  { value: 0.5, label: '0.5x' },
  { value: 0.75, label: '0.75x' },
  { value: 1, label: '1x' },
  { value: 1.25, label: '1.25x' },
  { value: 1.5, label: '1.5x' },
  { value: 1.75, label: '1.75x' },
  { value: 2, label: '2x' },
];
